<mat-expansion-panel [expanded]="true" #panel hideToggle="true" data-test-id="Process Issues" class="issues-panel">
  <mat-expansion-panel-header [collapsedHeight]="'auto'" [expandedHeight]="'auto'">
    <mat-panel-title class="my-3">
      <h1 class="m-0">Process Issues</h1>
    </mat-panel-title>
    <mat-panel-description class="justify-content-end flex-grow-0 m-0">
      <mat-icon>{{ panel.expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</mat-icon>
    </mat-panel-description>
  </mat-expansion-panel-header>
  <div class="panel-body">
    <div class="row">
      <div class="col-12">
        <div class="d-flex justify-content-end mb-2">
          <mat-form-field class="input-normalize">
            <input type="text" matInput [formControl]="formSearch" placeholder="Search" />
            <mat-icon matSuffix>search</mat-icon>
          </mat-form-field>
        </div>
        <div class="table-content">
          @if (isLoading()) {
            <mat-progress-bar mode="query"></mat-progress-bar>
          }

          <table mat-table matSort [dataSource]="(dataList$ | async) ?? []" class="mat-elevation-z8">
            <!-- Customer Column -->
            <ng-container matColumnDef="customer_name">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                sortActionDescription="Sort by customer name"
                class="header-border-right fw-semibold"
              >
                Customer
              </th>
              <td mat-cell *matCellDef="let element" class="table-elem-border-right">
                {{ element.customer_name }}
              </td>
            </ng-container>

            <!-- Object Type Column -->
            <ng-container matColumnDef="object_type">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                sortActionDescription="Sort by object type"
                class="header-border-right fw-semibold"
              >
                Object
              </th>
              <td mat-cell *matCellDef="let element" class="table-elem-border-right">
                {{ element.object_type }}
              </td>
            </ng-container>

            <!-- Object Name Column -->
            <ng-container matColumnDef="object_name">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                sortActionDescription="Sort by object name"
                class="header-border-right fw-semibold object-name"
              >
                Name
              </th>
              <td
                mat-cell
                *matCellDef="let element"
                class="table-elem-border-right text-break-word"
                [matTooltip]="
                  element.object_type === 'Connector' && element.pipeline_names?.length
                    ? getPipelineTooltip(element.pipeline_names)
                    : ''
                "
                [matTooltipDisabled]="element.object_type !== 'Connector' || !element.pipeline_names?.length"
                matTooltipClass="custom-tooltip pipeline-tooltip"
              >
                {{ element.object_name }}
              </td>
            </ng-container>

            <!-- Status Column -->
            <ng-container matColumnDef="status">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                sortActionDescription="Sort by status"
                class="fw-semibold"
              >
                Status
              </th>
              <td mat-cell *matCellDef="let element" class="status-column">
                <mat-chip-set
                  aria-label="Status"
                  class="status-chip chip-size-m"
                  [ngClass]="
                    element.status === 'APPROVED' && element.object_type.toLowerCase() === 'pipeline'
                      ? (element.status + 'pipeline' | statusColor)
                      : (element.status | statusColor)
                  "
                >
                  <mat-chip
                    [matTooltip]="element.last_run_error_message"
                    [matTooltipDisabled]="isTooltipDisabled(element.object_type.toLowerCase(), element.status)"
                  >
                    {{ element.status | serverStringConverter: 'processIssues' }}
                  </mat-chip>
                </mat-chip-set>
              </td>
            </ng-container>

            <!-- Last run time/date -->
            <ng-container matColumnDef="last_run_datetime">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                sortActionDescription="Sort by last run"
                class="fw-semibold"
              >
                Last run date/time
              </th>
              <td mat-cell *matCellDef="let element" class="text-ellipsis">
                {{ element.last_run_datetime | dateFormat }}
              </td>
            </ng-container>

            <ng-container matColumnDef="actions" stickyEnd>
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let element" class="table-actions-column-small" stopPropagationAction>
                <div
                  class="d-flex align-items-center justify-content-between"
                  [matTooltip]="'No open ticket assigned.'"
                  [matTooltipDisabled]="element.zendesk_ticket_url"
                  (click)="redirectToZendeskTicket(element.zendesk_ticket_url)"
                >
                  <button mat-icon-button [disabled]="!element.zendesk_ticket_url">
                    <mat-icon>support_agent</mat-icon>
                  </button>
                </div>
              </td>
            </ng-container>

            <!-- Filter row columns -->
            <ng-container matColumnDef="filter_customer_name">
              <th mat-header-cell *matHeaderCellDef class="filter-cell header-border-right">
                <mat-form-field class="filter-field" subscriptSizing="dynamic">
                  <input matInput [formControl]="filterCustomerName" placeholder="Filter..." />
                </mat-form-field>
              </th>
            </ng-container>

            <ng-container matColumnDef="filter_object_type">
              <th mat-header-cell *matHeaderCellDef class="filter-cell header-border-right">
                <mat-form-field class="filter-field" subscriptSizing="dynamic">
                  <mat-select [formControl]="filterObjectType" placeholder="All">
                    <mat-option value="">All</mat-option>
                    @for (type of objectTypeOptions; track type) {
                      <mat-option [value]="type">{{ type }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </th>
            </ng-container>

            <ng-container matColumnDef="filter_object_name">
              <th mat-header-cell *matHeaderCellDef class="filter-cell header-border-right">
                <mat-form-field class="filter-field" subscriptSizing="dynamic">
                  <input matInput [formControl]="filterObjectName" placeholder="Filter..." />
                </mat-form-field>
              </th>
            </ng-container>

            <ng-container matColumnDef="filter_status">
              <th mat-header-cell *matHeaderCellDef class="filter-cell">
                <mat-form-field class="filter-field" subscriptSizing="dynamic">
                  <mat-select [formControl]="filterStatus" placeholder="All">
                    <mat-option value="">All</mat-option>
                    @for (s of statusOptions; track s.value) {
                      <mat-option [value]="s.value">{{ s.label }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </th>
            </ng-container>

            <ng-container matColumnDef="filter_last_run_datetime">
              <th mat-header-cell *matHeaderCellDef class="filter-cell"></th>
            </ng-container>

            <ng-container matColumnDef="filter_actions" stickyEnd>
              <th mat-header-cell *matHeaderCellDef class="filter-cell"></th>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
            <tr mat-header-row *matHeaderRowDef="filterColumns; sticky: true" class="filter-row"></tr>
            <tr
              mat-row
              class="row-expandable"
              *matRowDef="let row; columns: displayedColumns"
              (click)="
                navigateToItem(
                  row.id,
                  row.customer_id,
                  row.object_type,
                  row.approval_status ? row.approval_status : null
                )
              "
            ></tr>
            <tr class="mat-row" *matNoDataRow>
              <td class="mat-cell" colspan="6">
                @if (isLoading()) {
                  <div class="empty-table">
                    <div class="empty-table-message-info">Loading...</div>
                  </div>
                } @else {
                  <div class="empty-table">
                    <div class="empty-table-message-info">No results found</div>
                  </div>
                }
              </td>
            </tr>
          </table>
        </div>
        <div class="mt-2">
          <mat-paginator
            [length]="resultsLength()"
            [pageSize]="pageSize()"
            [pageIndex]="currentPage() - 1"
            (page)="onPageChange($event)"
            [pageSizeOptions]="[10, 20, 30]"
            [showFirstLastButtons]="true"
            aria-label="Select page of results"
            class="paginator"
          ></mat-paginator>
        </div>
      </div>
    </div>
  </div>
</mat-expansion-panel>
